import React, { useEffect, useState } from "react";
import axios from "axios";
import "./AppHome.css";
import "../Admin/Admin.css";
import Header from "../Home/Header";
import { useNavigate } from "react-router-dom";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

const BASE_URL = "https://gym-invoice-back.onrender.com";

function PaymentAnalytics() {
  const [payments, setPayments] = useState([]);
  const [members, setMembers] = useState([]);

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    memberId: "",
    status: "",
  });

  // =========================================================
  // SELECTED BRANCH
  // =========================================================

  const [selectedBranch, setSelectedBranch] = useState("KB");

  const navigate = useNavigate();

  // =========================================================
  // LOGIN CHECK
  // =========================================================

  useEffect(() => {
    const role = sessionStorage.getItem("userRole");

    if (!role) {
      navigate("/");
    }
  }, [navigate]);

  // =========================================================
  // FETCH DATA
  // =========================================================

  useEffect(() => {
    fetchPayments();
    fetchMembers();
  }, []);

  // =========================================================
  // DEFAULT DATE RANGE - CURRENT MONTH
  // =========================================================

  useEffect(() => {
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(
        date.getMonth() + 1
      ).padStart(2, "0");

      const day = String(
        date.getDate()
      ).padStart(2, "0");

      return `${year}-${month}-${day}`;
    };

    const now = new Date();

    const firstDay = formatDate(
      new Date(
        now.getFullYear(),
        now.getMonth(),
        1
      )
    );

    const lastDay = formatDate(
      new Date(
        now.getFullYear(),
        now.getMonth() + 1,
        0
      )
    );

    setFilters((prev) => ({
      ...prev,
      fromDate: firstDay,
      toDate: lastDay,
    }));
  }, []);

  // =========================================================
  // FETCH PAYMENTS
  // =========================================================

  const fetchPayments = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/payments`
      );

      setPayments(
        Array.isArray(res.data)
          ? res.data
          : []
      );
    } catch (error) {
      console.error(
        "Failed to fetch payments:",
        error
      );
    }
  };

  // =========================================================
  // FETCH MEMBERS
  // =========================================================

  const fetchMembers = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/members`
      );

      setMembers(
        Array.isArray(res.data)
          ? res.data
          : []
      );
    } catch (error) {
      console.error(
        "Failed to fetch members:",
        error
      );
    }
  };

  // =========================================================
  // HANDLE FILTER CHANGE
  // =========================================================

  const handleFilterChange = (e) => {
    const {
      name,
      value,
    } = e.target;

    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =========================================================
  // PARSE LOCAL DATE
  // =========================================================

  const parseLocalDate = (dateStr) => {
    if (!dateStr) {
      return null;
    }

    const parts = dateStr
      .split("-")
      .map(Number);

    if (parts.length !== 3) {
      return null;
    }

    const [
      year,
      month,
      day,
    ] = parts;

    return new Date(
      year,
      month - 1,
      day
    );
  };

  // =========================================================
  // GET PAYMENT DATE
  // =========================================================

  const getPaymentDate = (payment) => {
    return (
      payment.payDate ||
      payment.date ||
      null
    );
  };

  // =========================================================
  // COMMON DATE FILTER FOR PAYMENT
  // =========================================================

  const paymentMatchesDate = (payment) => {
    const paymentDateString =
      getPaymentDate(payment);

    if (!paymentDateString) {
      return false;
    }

    const paymentDate =
      parseLocalDate(
        paymentDateString
      );

    if (!paymentDate) {
      return false;
    }

    const fromDate =
      filters.fromDate
        ? parseLocalDate(
            filters.fromDate
          )
        : null;

    const toDate =
      filters.toDate
        ? parseLocalDate(
            filters.toDate
          )
        : null;

    if (
      fromDate &&
      paymentDate < fromDate
    ) {
      return false;
    }

    if (
      toDate &&
      paymentDate > toDate
    ) {
      return false;
    }

    return true;
  };

  // =========================================================
  // COMMON DATE FILTER FOR MEMBER
  // =========================================================

  const memberMatchesDate = (member) => {
    if (!member.joinedDate) {
      return false;
    }

    const joinedDate =
      parseLocalDate(
        member.joinedDate
      );

    if (!joinedDate) {
      return false;
    }

    const fromDate =
      filters.fromDate
        ? parseLocalDate(
            filters.fromDate
          )
        : null;

    const toDate =
      filters.toDate
        ? parseLocalDate(
            filters.toDate
          )
        : null;

    if (
      fromDate &&
      joinedDate < fromDate
    ) {
      return false;
    }

    if (
      toDate &&
      joinedDate > toDate
    ) {
      return false;
    }

    return true;
  };

  // =========================================================
  // FILTERED PAYMENTS - SELECTED BRANCH
  // =========================================================

  const filteredPayments =
    payments.filter((payment) => {
      if (
        !paymentMatchesDate(payment)
      ) {
        return false;
      }

      // Branch filter
      if (
        selectedBranch &&
        payment.branchCode?.toUpperCase() !==
          selectedBranch.toUpperCase()
      ) {
        return false;
      }

      // Member ID
      if (
        filters.memberId &&
        !payment.memberId
          ?.toLowerCase()
          .includes(
            filters.memberId.toLowerCase()
          )
      ) {
        return false;
      }

      // Status
      if (
        filters.status &&
        !payment.status
          ?.toLowerCase()
          .includes(
            filters.status.toLowerCase()
          )
      ) {
        return false;
      }

      return true;
    });

  // =========================================================
  // ALL PAYMENTS - NO BRANCH FILTER
  // Used for Full Total
  // =========================================================

  const allFilteredPayments =
    payments.filter((payment) => {
      if (
        !paymentMatchesDate(payment)
      ) {
        return false;
      }

      if (
        filters.memberId &&
        !payment.memberId
          ?.toLowerCase()
          .includes(
            filters.memberId.toLowerCase()
          )
      ) {
        return false;
      }

      if (
        filters.status &&
        !payment.status
          ?.toLowerCase()
          .includes(
            filters.status.toLowerCase()
          )
      ) {
        return false;
      }

      return true;
    });

  // =========================================================
  // SELECTED BRANCH MEMBERS
  // =========================================================

  const filteredMembers =
    members.filter((member) => {
      if (
        !memberMatchesDate(member)
      ) {
        return false;
      }

      if (
        selectedBranch &&
        member.branchCode?.toUpperCase() !==
          selectedBranch.toUpperCase()
      ) {
        return false;
      }

      return true;
    });

  // =========================================================
  // ALL MEMBERS - NO BRANCH FILTER
  // Used for Full Total
  // =========================================================

  const allFilteredMembers =
    members.filter((member) => {
      return memberMatchesDate(
        member
      );
    });

  // =========================================================
  // SELECTED BRANCH PAYMENT REVENUE
  // =========================================================

  const totalPaymentRevenue =
    filteredPayments.reduce(
      (total, payment) => {
        const amount = Number(
          payment.amount
        );

        return (
          total +
          (Number.isFinite(amount)
            ? amount
            : 0)
        );
      },
      0
    );

  // =========================================================
  // FULL TOTAL PAYMENT REVENUE
  // =========================================================

  const fullTotalPaymentRevenue =
    allFilteredPayments.reduce(
      (total, payment) => {
        const amount = Number(
          payment.amount
        );

        return (
          total +
          (Number.isFinite(amount)
            ? amount
            : 0)
        );
      },
      0
    );

  // =========================================================
  // SELECTED BRANCH REGISTRATION MEMBERS
  //
  // Uses actual regFee from MongoDB.
  // No regStatus condition.
  // =========================================================

  const registeredMembers =
    filteredMembers.filter(
      (member) => {
        const regFee = Number(
          member.regFee
        );

        return (
          Number.isFinite(regFee) &&
          regFee > 0
        );
      }
    );

  // =========================================================
  // SELECTED BRANCH REGISTRATION FEE
  // =========================================================

  const totalRegFee =
    registeredMembers.reduce(
      (total, member) => {
        const regFee = Number(
          member.regFee
        );

        return (
          total +
          (Number.isFinite(regFee)
            ? regFee
            : 0)
        );
      },
      0
    );

  // =========================================================
  // FULL TOTAL REGISTRATION MEMBERS
  // =========================================================

  const allRegisteredMembers =
    allFilteredMembers.filter(
      (member) => {
        const regFee = Number(
          member.regFee
        );

        return (
          Number.isFinite(regFee) &&
          regFee > 0
        );
      }
    );

  // =========================================================
  // FULL TOTAL REGISTRATION FEE
  // =========================================================

  const fullTotalRegFee =
    allRegisteredMembers.reduce(
      (total, member) => {
        const regFee = Number(
          member.regFee
        );

        return (
          total +
          (Number.isFinite(regFee)
            ? regFee
            : 0)
        );
      },
      0
    );

  // =========================================================
  // SELECTED BRANCH TOTAL REVENUE
  // =========================================================

  const totalRevenue =
    totalPaymentRevenue +
    totalRegFee;

  // =========================================================
  // FULL TOTAL REVENUE
  // =========================================================

  const fullTotalRevenue =
    fullTotalPaymentRevenue +
    fullTotalRegFee;

  // =========================================================
  // SELECTED BRANCH ACTIVE MEMBERS
  // =========================================================

  const activeCount =
    filteredMembers.filter(
      (member) =>
        member.membershipStatus
          ?.toLowerCase() ===
        "active"
    ).length;

  // =========================================================
  // SELECTED BRANCH INACTIVE MEMBERS
  // =========================================================

  const inactiveCount =
    filteredMembers.filter(
      (member) =>
        member.membershipStatus
          ?.toLowerCase() !==
        "active"
    ).length;

  // =========================================================
  // FULL TOTAL ACTIVE MEMBERS
  // =========================================================

  const fullActiveCount =
    allFilteredMembers.filter(
      (member) =>
        member.membershipStatus
          ?.toLowerCase() ===
        "active"
    ).length;

  // =========================================================
  // FULL TOTAL INACTIVE MEMBERS
  // =========================================================

  const fullInactiveCount =
    allFilteredMembers.filter(
      (member) =>
        member.membershipStatus
          ?.toLowerCase() !==
        "active"
    ).length;

  // =========================================================
  // SELECTED BRANCH MEMBERSHIP COUNTS
  // =========================================================

  const membershipCounts =
    filteredMembers
      .filter(
        (member) =>
          member.membershipStatus
            ?.toLowerCase() ===
          "active"
      )
      .reduce(
        (acc, member) => {
          if (
            !member.membershipType
          ) {
            return acc;
          }

          if (
            !acc[
              member.membershipType
            ]
          ) {
            acc[
              member.membershipType
            ] = 0;
          }

          acc[
            member.membershipType
          ]++;

          return acc;
        },
        {}
      );

  // =========================================================
  // FULL MEMBERSHIP COUNTS
  // =========================================================

  const fullMembershipCounts =
    allFilteredMembers
      .filter(
        (member) =>
          member.membershipStatus
            ?.toLowerCase() ===
          "active"
      )
      .reduce(
        (acc, member) => {
          if (
            !member.membershipType
          ) {
            return acc;
          }

          if (
            !acc[
              member.membershipType
            ]
          ) {
            acc[
              member.membershipType
            ] = 0;
          }

          acc[
            member.membershipType
          ]++;

          return acc;
        },
        {}
      );

  // =========================================================
  // SELECTED BRANCH MEMBERSHIP-WISE REVENUE
  // =========================================================

  const membershipWiseRevenue =
    filteredPayments.reduce(
      (acc, payment) => {
        if (
          !payment.membershipType
        ) {
          return acc;
        }

        if (
          !acc[
            payment.membershipType
          ]
        ) {
          acc[
            payment.membershipType
          ] = 0;
        }

        const amount = Number(
          payment.amount
        );

        acc[
          payment.membershipType
        ] += Number.isFinite(amount)
          ? amount
          : 0;

        return acc;
      },
      {}
    );

  // =========================================================
  // FULL MEMBERSHIP-WISE REVENUE
  // =========================================================

  const fullMembershipWiseRevenue =
    allFilteredPayments.reduce(
      (acc, payment) => {
        if (
          !payment.membershipType
        ) {
          return acc;
        }

        if (
          !acc[
            payment.membershipType
          ]
        ) {
          acc[
            payment.membershipType
          ] = 0;
        }

        const amount = Number(
          payment.amount
        );

        acc[
          payment.membershipType
        ] += Number.isFinite(amount)
          ? amount
          : 0;

        return acc;
      },
      {}
    );

  // =========================================================
  // SELECTED BRANCH MONTHLY REVENUE
  // =========================================================

  const monthlyIncome =
    filteredPayments.reduce(
      (acc, payment) => {
        const paymentDate =
          getPaymentDate(payment);

        if (!paymentDate) {
          return acc;
        }

        const monthKey =
          paymentDate.slice(0, 7);

        if (!acc[monthKey]) {
          acc[monthKey] = 0;
        }

        const amount = Number(
          payment.amount
        );

        acc[monthKey] +=
          Number.isFinite(amount)
            ? amount
            : 0;

        return acc;
      },
      {}
    );

  const chartData = Object.entries(
    monthlyIncome
  )
    .sort(([a], [b]) =>
      a.localeCompare(b)
    )
    .map(
      ([month, amount]) => ({
        month,
        amount,
      })
    );

  // =========================================================
  // FULL MONTHLY REVENUE
  // =========================================================

  const fullMonthlyIncome =
    allFilteredPayments.reduce(
      (acc, payment) => {
        const paymentDate =
          getPaymentDate(payment);

        if (!paymentDate) {
          return acc;
        }

        const monthKey =
          paymentDate.slice(0, 7);

        if (!acc[monthKey]) {
          acc[monthKey] = 0;
        }

        const amount = Number(
          payment.amount
        );

        acc[monthKey] +=
          Number.isFinite(amount)
            ? amount
            : 0;

        return acc;
      },
      {}
    );

  const fullChartData =
    Object.entries(
      fullMonthlyIncome
    )
      .sort(([a], [b]) =>
        a.localeCompare(b)
      )
      .map(
        ([month, amount]) => ({
          month,
          amount,
        })
      );

  // =========================================================
  // BRANCH NAME
  // =========================================================

  const selectedBranchName =
    selectedBranch === "KB"
      ? "KB - Kesbewa"
      : "WB - Werahera";

  // =========================================================
  // CHANGE BRANCH
  // =========================================================

  const handleBranchChange = (e) => {
    const value =
      e.target.value.toUpperCase();

    setSelectedBranch(value);
  };

  // =========================================================
  // NUMBER FORMAT
  // =========================================================

  const formatAmount = (value) => {
    return Number(
      value || 0
    ).toLocaleString("en-LK", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // =========================================================
  // RETURN
  // =========================================================

  return (
    <div className="dashboard">

      <Header />

      <div className="payment-container">

        <h2>
          💰 Payment Analytics Dashboard
        </h2>

        {/* =====================================================
            FILTERS
        ====================================================== */}

        <div
          className="payment-card"
          style={{
            width: "100%",
            boxSizing: "border-box",
          }}
        >

          <label>
            Select Date From
          </label>

          <input
            type="date"
            name="fromDate"
            value={filters.fromDate}
            onChange={
              handleFilterChange
            }
          />

          <label>
            Select Date To
          </label>

          <input
            type="date"
            name="toDate"
            value={filters.toDate}
            onChange={
              handleFilterChange
            }
          />

          <label>
            Member ID
          </label>

          <input
            type="text"
            name="memberId"
            placeholder="Member ID"
            value={filters.memberId}
            onChange={
              handleFilterChange
            }
          />

        </div>


        {/* =====================================================
            FULL TOTAL
        ====================================================== */}

        <h3>
          Full Total - All Branches
        </h3>

        <div className="analytics-cards">

          <div className="card">
            <h4>
              Total Revenue
            </h4>

            <p>
              Rs{" "}
              {formatAmount(
                fullTotalRevenue
              )}
            </p>
          </div>

          <div className="card">
            <h4>
              Total Membership Payments
            </h4>

            <p>
              Rs{" "}
              {formatAmount(
                fullTotalPaymentRevenue
              )}
            </p>
          </div>

          <div className="card">
            <h4>
              Total Registration Fees
            </h4>

            <p>
              Rs{" "}
              {formatAmount(
                fullTotalRegFee
              )}
            </p>
          </div>

          <div className="card">
            <h4>
              Active Members
            </h4>

            <p>
              {fullActiveCount}
            </p>
          </div>

          <div className="card">
            <h4>
              Inactive Members
            </h4>

            <p>
              {fullInactiveCount}
            </p>
          </div>

        </div>

        {/* =====================================================
            SELECTED BRANCH TOTAL
        ====================================================== */}

        {/* =====================================================
                    BRANCH SELECTOR
                ====================================================== */}

                <div
                  className="branch-selector"
                  style={{
                    width: "100%",
                    marginTop: "15px",
                    marginBottom: "20px",
                    boxSizing: "border-box",
                  }}
                >

                  <label
                    htmlFor="branchSelect"
                    style={{
                      display: "block",
                      fontWeight: "bold",
                      marginBottom: "8px",
                    }}
                  >
                    Select Branch
                  </label>

                  <select
                    id="branchSelect"
                    value={selectedBranch}
                    onChange={
                      handleBranchChange
                    }
                    style={{
                      width: "100%",
                      minHeight: "46px",
                      padding: "10px 12px",
                      fontSize: "16px",
                      borderRadius: "6px",
                      border:
                        "1px solid #ccc",
                      backgroundColor:
                        "#ffffff",
                      boxSizing:
                        "border-box",
                      cursor: "pointer",
                    }}
                  >

                    <option value="KB">
                      KB - Kesbewa
                    </option>

                    <option value="WB">
                      WB - Werahera
                    </option>

                  </select>

                </div>

        {/* =====================================================
            SELECTED BRANCH DETAILS
        ====================================================== */}

        <h3>
          {selectedBranchName} Details
        </h3>

        <div
          style={{
            width: "100%",
            overflowX: "auto",
          }}
        >

          <table
            className="payment-table"
            style={{
              width: "100%",
              minWidth: "500px",
            }}
          >

            <thead>

              <tr>
                <th>
                  Details
                </th>

                <th>
                  Amount / Count
                </th>
              </tr>

            </thead>

            <tbody>

              <tr>
                <td>
                  Total Revenue
                </td>

                <td>
                  Rs{" "}
                  {formatAmount(
                    totalRevenue
                  )}
                </td>
              </tr>

              <tr>
                <td>
                  Membership Payments
                </td>

                <td>
                  Rs{" "}
                  {formatAmount(
                    totalPaymentRevenue
                  )}
                </td>
              </tr>

              <tr>
                <td>
                  Registration Fees
                </td>

                <td>
                  Rs{" "}
                  {formatAmount(
                    totalRegFee
                  )}
                </td>
              </tr>

              <tr>
                <td>
                  Active Members
                </td>

                <td>
                  {activeCount}
                </td>
              </tr>

              <tr>
                <td>
                  Inactive Members
                </td>

                <td>
                  {inactiveCount}
                </td>
              </tr>

            </tbody>

          </table>

        </div>

        {/* =====================================================
            FULL MEMBERSHIP-WISE REVENUE
        ====================================================== */}

        <h3>
          Full Membership Wise Revenue
        </h3>

        <div
          style={{
            width: "100%",
            overflowX: "auto",
          }}
        >

          <table
            className="payment-table"
            style={{
              width: "100%",
              minWidth: "500px",
            }}
          >

            <thead>

              <tr>
                <th>
                  Membership Type
                </th>

                <th>
                  Total Revenue (Rs)
                </th>
              </tr>

            </thead>

            <tbody>

              {Object.entries(
                fullMembershipWiseRevenue
              ).length === 0 ? (

                <tr>
                  <td colSpan="2">
                    No payment data found.
                  </td>
                </tr>

              ) : (

                Object.entries(
                  fullMembershipWiseRevenue
                ).map(
                  ([type, amount]) => (
                    <tr key={type}>
                      <td>
                        {type}
                      </td>

                      <td>
                        {formatAmount(
                          amount
                        )}
                      </td>
                    </tr>
                  )
                )

              )}

            </tbody>

          </table>

        </div>

        {/* =====================================================
            SELECTED BRANCH MEMBERSHIP-WISE REVENUE
        ====================================================== */}

        <h3>
          {selectedBranchName} Membership Wise Revenue
        </h3>

        <div
          style={{
            width: "100%",
            overflowX: "auto",
          }}
        >

          <table
            className="payment-table"
            style={{
              width: "100%",
              minWidth: "500px",
            }}
          >

            <thead>

              <tr>
                <th>
                  Membership Type
                </th>

                <th>
                  Total Revenue (Rs)
                </th>
              </tr>

            </thead>

            <tbody>

              {Object.entries(
                membershipWiseRevenue
              ).length === 0 ? (

                <tr>
                  <td colSpan="2">
                    No payment data found.
                  </td>
                </tr>

              ) : (

                Object.entries(
                  membershipWiseRevenue
                ).map(
                  ([type, amount]) => (
                    <tr key={type}>
                      <td>
                        {type}
                      </td>

                      <td>
                        {formatAmount(
                          amount
                        )}
                      </td>
                    </tr>
                  )
                )

              )}

            </tbody>

          </table>

        </div>

        {/* =====================================================
            FULL MEMBERSHIP COUNT
        ====================================================== */}

        <h3>
          Full Membership Count - All Branches
        </h3>

        <div
          style={{
            width: "100%",
            overflowX: "auto",
          }}
        >

          <table
            className="payment-table"
            style={{
              width: "100%",
              minWidth: "500px",
            }}
          >

            <thead>

              <tr>
                <th>
                  Membership Type
                </th>

                <th>
                  Count
                </th>
              </tr>

            </thead>

            <tbody>

              {Object.entries(
                fullMembershipCounts
              ).length === 0 ? (

                <tr>
                  <td colSpan="2">
                    No members found.
                  </td>
                </tr>

              ) : (

                Object.entries(
                  fullMembershipCounts
                ).map(
                  ([type, count]) => (
                    <tr key={type}>
                      <td>
                        {type}
                      </td>

                      <td>
                        {count}
                      </td>
                    </tr>
                  )
                )

              )}

            </tbody>

          </table>

        </div>

        {/* =====================================================
            SELECTED BRANCH MEMBERSHIP COUNT
        ====================================================== */}

        <h3>
          {selectedBranchName} Membership Count
        </h3>

        <div
          style={{
            width: "100%",
            overflowX: "auto",
          }}
        >

          <table
            className="payment-table"
            style={{
              width: "100%",
              minWidth: "500px",
            }}
          >

            <thead>

              <tr>
                <th>
                  Membership Type
                </th>

                <th>
                  Count
                </th>
              </tr>

            </thead>

            <tbody>

              {Object.entries(
                membershipCounts
              ).length === 0 ? (

                <tr>
                  <td colSpan="2">
                    No members found.
                  </td>
                </tr>

              ) : (

                Object.entries(
                  membershipCounts
                ).map(
                  ([type, count]) => (
                    <tr key={type}>
                      <td>
                        {type}
                      </td>

                      <td>
                        {count}
                      </td>
                    </tr>
                  )
                )

              )}

            </tbody>

          </table>

        </div>

        {/* =====================================================
            FULL MONTHLY REVENUE
        ====================================================== */}

        <h3>
          Full Monthly Revenue Trend
        </h3>

        <div
          style={{
            width: "100%",
            overflow: "hidden",
          }}
        >

          <ResponsiveContainer
            width="100%"
            height={300}
          >

            <LineChart
              data={fullChartData}
              margin={{
                top: 20,
                right: 20,
                left: 10,
                bottom: 5,
              }}
            >

              <CartesianGrid
                strokeDasharray="3 3"
              />

              <XAxis
                dataKey="month"
              />

              <YAxis />

              <Tooltip
                formatter={(value) =>
                  `Rs ${formatAmount(
                    value
                  )}`
                }
              />

              <Line
                type="monotone"
                dataKey="amount"
                stroke="#008080"
                strokeWidth={2}
              />

            </LineChart>

          </ResponsiveContainer>

        </div>

        {/* =====================================================
            SELECTED BRANCH MONTHLY REVENUE
        ====================================================== */}

        <h3>
          {selectedBranchName} Monthly Revenue Trend
        </h3>

        <div
          style={{
            width: "100%",
            overflow: "hidden",
          }}
        >

          <ResponsiveContainer
            width="100%"
            height={300}
          >

            <LineChart
              data={chartData}
              margin={{
                top: 20,
                right: 20,
                left: 10,
                bottom: 5,
              }}
            >

              <CartesianGrid
                strokeDasharray="3 3"
              />

              <XAxis
                dataKey="month"
              />

              <YAxis />

              <Tooltip
                formatter={(value) =>
                  `Rs ${formatAmount(
                    value
                  )}`
                }
              />

              <Line
                type="monotone"
                dataKey="amount"
                stroke="#008080"
                strokeWidth={2}
              />

            </LineChart>

          </ResponsiveContainer>

        </div>

      </div>
    </div>
  );
}

export default PaymentAnalytics;